<template>
  <el-dialog
    title="キャンペーン検索"
    :visible="visible"
    width="1120px"
    custom-class="with-title tw-benchmark-filter-dialog"
    @update:visible="$emit('close')"
  >
    <div class="tw-benchmark-filter">
      <!-- アカウント -->
      <section class="tw-benchmark-filter__group">
        <h4 class="tw-benchmark-filter__group-title">アカウント</h4>
        <div class="tw-benchmark-filter__row">
          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">アカウント検索</span>
            <el-select
              v-model="accountIdsModel"
              size="small"
              multiple
              filterable
              remote
              clearable
              collapse-tags
              placeholder=""
              class="tw-benchmark-filter__collapse-select"
              :no-data-text="accountNoDataText"
              :remote-method="searchAccounts"
              :loading="accountLoading"
              @visible-change="onAccountVisibleChange"
              @change="onChangeAccountIds"
            >
              <el-option
                v-for="account in accountOptions"
                :key="account.id"
                :label="accountTagLabel(account.screenName)"
                :value="account.id"
                class="tw-benchmark-filter__account-option"
              >
                <img
                  v-if="account.pictureUrl"
                  :src="account.pictureUrl"
                  alt=""
                  class="tw-benchmark-filter__account-icon"
                />
                <span class="tw-benchmark-filter__account-name">{{ account.name }}</span>
                <span class="tw-benchmark-filter__account-screen">@{{ account.screenName }}</span>
              </el-option>
            </el-select>
          </div>

          <FilterRange
            label="フォロワー数"
            :min-value="filters.followersMin"
            :max-value="filters.followersMax"
            :min-error="errors.followersMin"
            :max-error="errors.followersMax"
            @update:minValue="onNumericInput('followersMin', $event)"
            @update:maxValue="onNumericInput('followersMax', $event)"
          />

          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">タグ</span>
            <el-select
              v-model="tagsModel"
              size="small"
              multiple
              filterable
              collapse-tags
              clearable
              placeholder=""
              class="tw-benchmark-filter__collapse-select"
              @change="onChangeTags"
            >
              <el-option v-for="tag in tagOptions" :key="tag.id" :label="tag.name" :value="tag.id">
                <span :title="tag.name">{{ truncateLabel(tag.name) }}</span>
              </el-option>
            </el-select>
          </div>
        </div>
      </section>

      <!-- キャンペーン内容 -->
      <section class="tw-benchmark-filter__group">
        <h4 class="tw-benchmark-filter__group-title">キャンペーン内容</h4>
        <div class="tw-benchmark-filter__row">
          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">投稿内容</span>
            <el-input v-model="keyword" size="small" clearable />
            <span class="tw-benchmark-filter__error">{{ errors.keyword }}</span>
          </div>

          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">キャンペーン形式</span>
            <el-select
              v-model="campaignTypesModel"
              size="small"
              multiple
              collapse-tags
              clearable
              placeholder=""
              class="tw-benchmark-filter__collapse-select"
              @change="onChangeCampaignTypes"
            >
              <el-option
                v-for="option in campaignTypeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>

          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">インセンティブ</span>
            <el-select
              v-model="incentivesModel"
              size="small"
              multiple
              collapse-tags
              clearable
              placeholder=""
              class="tw-benchmark-filter__collapse-select"
            >
              <el-option
                v-for="option in incentiveOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
        </div>

        <div class="tw-benchmark-filter__row">
          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">当選人数</span>
            <el-select v-model="winnerCountRangeModel" size="small" clearable placeholder="">
              <el-option
                v-for="option in winnerCountRangeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>

          <div class="tw-benchmark-filter__field">
            <span class="tw-benchmark-filter__label">トレンド実績</span>
            <el-select v-model="isTrendModel" size="small" clearable placeholder="">
              <el-option label="あり" :value="true" />
              <el-option label="なし" :value="false" />
            </el-select>
          </div>

          <FilterRange
            label="開催日数"
            :min-value="filters.campaignDaysMin"
            :max-value="filters.campaignDaysMax"
            :min-error="errors.campaignDaysMin"
            :max-error="errors.campaignDaysMax"
            @update:minValue="onNumericInput('campaignDaysMin', $event)"
            @update:maxValue="onNumericInput('campaignDaysMax', $event)"
          />
        </div>
      </section>

      <!-- エンゲージメント -->
      <section class="tw-benchmark-filter__group">
        <h4 class="tw-benchmark-filter__group-title">エンゲージメント</h4>
        <div class="tw-benchmark-filter__row">
          <FilterRange
            v-for="range in engagementRanges"
            :key="range.base"
            :label="range.label"
            :min-value="filters[range.minKey]"
            :max-value="filters[range.maxKey]"
            :min-error="errors[range.minKey]"
            :max-error="errors[range.maxKey]"
            :decimal-scale="range.decimalScale"
            @update:minValue="onNumericInput(range.minKey, $event)"
            @update:maxValue="onNumericInput(range.maxKey, $event)"
          />
        </div>
      </section>

      <section v-loading="industryLoading" class="tw-benchmark-filter__group">
        <h4 class="tw-benchmark-filter__group-title">業界</h4>
        <div class="tw-benchmark-filter__industry-groups">
          <div v-for="group in industryGroups" :key="group.label" class="tw-benchmark-filter__industry-group">
            <div class="tw-benchmark-filter__industry-group-header">
              <el-checkbox
                :value="isIndustryGroupAllSelected(group)"
                :indeterminate="isIndustryGroupIndeterminate(group)"
                @change="onToggleIndustryGroup(group, $event)"
              >
                <span class="tw-benchmark-filter__industry-group-label">{{ group.label }}</span>
              </el-checkbox>
            </div>
            <div class="tw-benchmark-filter__industry-group-children">
              <el-checkbox
                v-for="child in group.children"
                :key="child.id"
                :value="industryIdsModel.includes(child.id)"
                @change="onToggleIndustryChild(child.id)"
              >
                {{ child.minorCategory }}
              </el-checkbox>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template slot="footer">
      <div class="tw-benchmark-filter-dialog-footer">
        <el-button size="small" plain @click="onClear"> <i class="mdi mdi-reload"></i>クリア </el-button>
        <el-button size="small" type="primary" :disabled="!isValid" @click="onSearch">
          <i class="mdi mdi-magnify"></i>検索
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { computed, defineComponent, onMounted, ref, watch } from 'vue'

import { getTwBenchmarkIndustries } from '@/client/features/twitter_benchmark_analytics/api/getTwBenchmarkIndustries'
import {
  campaignTypeOptions,
  incentiveOptions,
  resolveWinnerCountRangeValue,
  WINNER_COUNT_RANGE_OPTIONS
} from '@/client/features/twitter_benchmark_analytics/utils/campaignLabels'
import { truncateLabel } from '@/client/features/twitter_benchmark_analytics/utils/formatters'
import { formatAppliedFilterSuffix } from '@/client/features/twitter_benchmark_analytics/utils/searchFilterSummary'
import { TrackingService } from '@/client/services'
import { useSystemStore } from '@/client/store/useSystemStore'
import { type IndustryListResponse, type SearchFilters } from '@/typings/twitter_benchmark_campaign_posts'

import { useAccountSelectResolver } from '../../../hooks/useAccountSelectResolver'
import { useTwBenchmarkFilters, type NumericFilterKey } from '../../../hooks/useTwBenchmarkFilters'
import { useTwBenchmarkPostsStore } from '../../../store/useTwBenchmarkPostsStore'
import { useTwBenchmarkTagsStore } from '../../../store/useTwBenchmarkTagsStore'
import FilterRange from './FilterRange.vue'

type IndustryItem = IndustryListResponse['industries'][number]

type IndustryGroup = {
  label: string
  children: IndustryItem[]
}

const OTHER_MAJOR_CATEGORY = 'その他'

const ACCOUNT_SCREEN_NAME_MAX_LENGTH = 20

/** エンゲージメント指標の数値レンジ（すべて「以上〜未満」で表示）。 */
const ENGAGEMENT_RANGES: {
  base: string
  label: string
  minKey: NumericFilterKey
  maxKey: NumericFilterKey
  decimalScale?: number
}[] = [
  {
    base: 'impressionsCount',
    label: 'インプレッション',
    minKey: 'impressionsCountMin',
    maxKey: 'impressionsCountMax'
  },
  {
    base: 'likesCount',
    label: 'いいね',
    minKey: 'likesCountMin',
    maxKey: 'likesCountMax'
  },
  {
    base: 'retweetsCount',
    label: 'リポスト',
    minKey: 'retweetsCountMin',
    maxKey: 'retweetsCountMax'
  },
  {
    base: 'quotesCount',
    label: '引用数',
    minKey: 'quotesCountMin',
    maxKey: 'quotesCountMax'
  },
  {
    base: 'repliesCount',
    label: '返信数',
    minKey: 'repliesCountMin',
    maxKey: 'repliesCountMax'
  },
  {
    base: 'reactionsCount',
    label: '反応数',
    minKey: 'reactionsCountMin',
    maxKey: 'reactionsCountMax'
  },
  {
    base: 'reactionsRate',
    label: '反応率',
    minKey: 'reactionsRateMin',
    maxKey: 'reactionsRateMax',
    decimalScale: 2
  }
]

/** すべての数値レンジキー（クリア時の一括リセットに使用）。 */
const ALL_RANGE_KEYS: NumericFilterKey[] = [
  'followersMin',
  'followersMax',
  'campaignDaysMin',
  'campaignDaysMax',
  ...ENGAGEMENT_RANGES.flatMap(range => [range.minKey, range.maxKey])
]

/**
 * 検索フィルターパネル
 */
export default defineComponent({
  name: 'TwBenchmarkFilterPanel',

  components: {
    FilterRange
  },

  props: {
    startDatetime: {
      type: String,
      default: ''
    },
    endDatetime: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: false
    }
  },

  emits: ['search', 'clear', 'close'],

  setup(props, { emit }) {
    const postsStore = useTwBenchmarkPostsStore()
    const filtersState = useTwBenchmarkFilters(postsStore.searchFilters as SearchFilters)
    const { keyword, startDatetime, endDatetime, filters, errors, isValid, buildAppliedFilters, resetFromApplied } =
      filtersState

    const tagsStore = useTwBenchmarkTagsStore()
    const systemStore = useSystemStore()

    watch(
      () => [props.startDatetime, props.endDatetime] as const,
      ([start, end]) => {
        startDatetime.value = start
        endDatetime.value = end
      },
      { immediate: true }
    )

    // 検索せずに閉じた下書きが残らないよう、開くたびに適用中の条件でフォームを再初期化する
    watch(
      () => props.visible,
      visible => {
        if (visible) {
          resetFromApplied(postsStore.searchFilters as SearchFilters)
        }
      }
    )

    const isTrendModel = computed({
      get: () => filters.isTrend,
      set: (value: boolean | undefined) => {
        filters.isTrend = value
      }
    })

    // 当選人数はレンジ選択肢を単一選択させ、送信時は既存の winnerCountMin/Max・判別不可フラグへ変換する
    const winnerCountRangeModel = computed({
      get: () => resolveWinnerCountRangeValue(filters),
      set: (value: string | undefined) => {
        const option = WINNER_COUNT_RANGE_OPTIONS.find(item => item.value === value)
        filters.winnerCountUndetectable = option?.undetectable === true ? true : undefined
        filters.winnerCountMin = option?.min
        filters.winnerCountMax = option?.max
      }
    })

    const accountIdsModel = computed({
      get: () => filters.accountIds ?? [],
      set: (value: number[]) => {
        filters.accountIds = value.length ? value : undefined
      }
    })
    const tagsModel = computed({
      get: () => filters.tags ?? [],
      set: (value: number[]) => {
        filters.tags = value.length ? value : undefined
      }
    })
    const campaignTypesModel = computed({
      get: () => filters.campaignTypes ?? [],
      set: (value: number[]) => {
        filters.campaignTypes = value.length ? value : undefined
      }
    })
    const incentivesModel = computed({
      get: () => filters.incentives ?? [],
      set: (value: number[]) => {
        filters.incentives = value.length ? value : undefined
      }
    })
    const industryIdsModel = computed({
      get: () => filters.industryIds ?? [],
      set: (value: number[]) => {
        filters.industryIds = value.length ? value : undefined
      }
    })

    const industries = ref<IndustryItem[]>([])
    const industryLoading = ref(false)

    const industryGroups = computed<IndustryGroup[]>(() => {
      const groups: IndustryGroup[] = []
      const indexByLabel = new Map<string, number>()

      industries.value.forEach(industry => {
        const index = indexByLabel.get(industry.majorCategory)
        if (index === undefined) {
          indexByLabel.set(industry.majorCategory, groups.length)
          groups.push({ label: industry.majorCategory, children: [industry] })
        } else {
          groups[index].children.push(industry)
        }
      })

      const otherIndex = groups.findIndex(group => group.label === OTHER_MAJOR_CATEGORY)
      if (otherIndex === -1) return groups
      const [otherGroup] = groups.splice(otherIndex, 1)
      groups.push(otherGroup)
      return groups
    })

    const fetchIndustries = async (): Promise<void> => {
      const groupId = systemStore.currentGroupId
      if (typeof groupId !== 'number') return

      industryLoading.value = true
      try {
        const result = await getTwBenchmarkIndustries(groupId)
        industries.value = result.isSuccess && result.data ? result.data.industries : []
      } finally {
        industryLoading.value = false
      }
    }

    onMounted(fetchIndustries)

    const isIndustryGroupAllSelected = (group: IndustryGroup): boolean =>
      group.children.length > 0 && group.children.every(child => industryIdsModel.value.includes(child.id))

    const isIndustryGroupIndeterminate = (group: IndustryGroup): boolean => {
      const selectedCount = group.children.filter(child => industryIdsModel.value.includes(child.id)).length
      return selectedCount > 0 && selectedCount < group.children.length
    }

    const toggleIndustryChild = (id: number): void => {
      const current = industryIdsModel.value
      industryIdsModel.value = current.includes(id) ? current.filter(v => v !== id) : [...current, id]
    }

    const onToggleIndustryGroup = (group: IndustryGroup, checked: boolean): void => {
      TrackingService.sendEvent('select:競合キャンペーン分析|キャンペーン検索|業界')
      let next = industryIdsModel.value
      group.children.forEach(child => {
        const isSelected = next.includes(child.id)
        if (checked && !isSelected) {
          next = [...next, child.id]
        } else if (!checked && isSelected) {
          next = next.filter(v => v !== child.id)
        }
      })
      industryIdsModel.value = next
    }

    const onToggleIndustryChild = (id: number): void => {
      TrackingService.sendEvent('select:競合キャンペーン分析|キャンペーン検索|業界')
      toggleIndustryChild(id)
    }

    const groupIdRef = computed(() => systemStore.currentGroupId)
    const postsRef = computed(() => postsStore.posts)

    const {
      accountOptions,
      accountLoading,
      accountNoDataText,
      searchAccounts,
      resolveUnknownAccountIds,
      onAccountVisibleChange
    } = useAccountSelectResolver({ accountIds: accountIdsModel, groupId: groupIdRef, posts: postsRef })

    onMounted(resolveUnknownAccountIds)

    const accountTagLabel = (screenName: string): string =>
      `@${truncateLabel(screenName, ACCOUNT_SCREEN_NAME_MAX_LENGTH)}`

    const tagOptions = computed(() => tagsStore.tags)

    const onNumericInput = (key: NumericFilterKey, value: number | undefined): void => {
      filters[key] = value
    }

    const onSearch = (): void => {
      if (!isValid.value) return
      const applied: SearchFilters = buildAppliedFilters()
      TrackingService.sendEvent('click:競合キャンペーン分析|キャンペーン検索|検索')
      const suffix = formatAppliedFilterSuffix(applied)
      if (suffix) {
        TrackingService.sendEvent(`click:競合キャンペーン分析|キャンペーン検索条件内容|${suffix}`)
      }
      emit('search', applied)
      emit('close')
    }

    const onClear = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|キャンペーン検索|クリア')
      keyword.value = ''
      filters.isTrend = undefined
      filters.accountIds = undefined
      filters.tags = undefined
      filters.campaignTypes = undefined
      filters.incentives = undefined
      filters.industryIds = undefined
      filters.winnerCountUndetectable = undefined
      filters.winnerCountMin = undefined
      filters.winnerCountMax = undefined
      for (const key of ALL_RANGE_KEYS) {
        filters[key] = undefined
      }
      emit('clear')
    }

    const onChangeAccountIds = (): void => {
      TrackingService.sendEvent('select:競合キャンペーン分析|キャンペーン検索|アカウント検索')
    }

    const onChangeTags = (): void => {
      TrackingService.sendEvent('select:競合キャンペーン分析|キャンペーン検索|タグ')
    }

    const onChangeCampaignTypes = (): void => {
      TrackingService.sendEvent('select:競合キャンペーン分析|キャンペーン検索|キャンペーン形式')
    }

    return {
      keyword,
      filters,
      errors,
      isValid,
      isTrendModel,
      winnerCountRangeModel,
      winnerCountRangeOptions: WINNER_COUNT_RANGE_OPTIONS,
      accountIdsModel,
      tagsModel,
      campaignTypesModel,
      incentivesModel,
      accountOptions,
      accountLoading,
      accountTagLabel,
      accountNoDataText,
      searchAccounts,
      onAccountVisibleChange,
      tagOptions,
      truncateLabel,
      campaignTypeOptions,
      incentiveOptions,
      engagementRanges: ENGAGEMENT_RANGES,
      industryIdsModel,
      industryGroups,
      industryLoading,
      isIndustryGroupAllSelected,
      isIndustryGroupIndeterminate,
      onToggleIndustryGroup,
      onToggleIndustryChild,
      onNumericInput,
      onSearch,
      onClear,
      onChangeAccountIds,
      onChangeTags,
      onChangeCampaignTypes
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.tw-benchmark-filter-dialog) {
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  .el-dialog__header {
    flex-shrink: 0;
    border-bottom: 1px solid $border-color;
  }

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .el-dialog__footer {
    flex-shrink: 0;
    border-top: 1px solid $border-color;
  }
}

.tw-benchmark-filter {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__group-title {
    margin: 0;
    font-size: 14px;
    font-weight: bold;
    color: $text-color;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 260px;
  }

  &__label {
    font-size: 12px;
    color: $text-color;
  }

  &__error {
    height: 16px;
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    color: $danger-color;
  }

  &__collapse-select {
    :deep(.el-select__tags) {
      flex-wrap: nowrap;
      max-width: 100%;
      overflow: hidden;
    }

    :deep(.el-select__tags > span) {
      display: flex;
      align-items: center;
      max-width: 100%;
      overflow: hidden;

      .el-tag:first-child {
        min-width: 0;
        overflow: hidden;
      }

      .el-tag:nth-child(2) {
        flex-shrink: 0;
      }
    }

    :deep(.el-select__tags .el-tag) {
      max-width: 100%;

      .el-select__tags-text {
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: bottom;
        white-space: nowrap;
      }
    }
  }

  &__account-option {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__account-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    object-fit: cover;
    border-radius: 50%;
  }

  &__account-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__account-screen {
    flex-shrink: 0;
    color: $sub-text-color;
  }

  &__industry-groups {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit * 3;
    overflow-y: auto;

    :deep(.el-checkbox) {
      box-shadow: none;
    }
  }

  &__industry-group-header {
    margin-bottom: $spacing-unit * 2;
  }

  &__industry-group-children {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-unit;
    padding-left: $spacing-unit * 3;

    :deep(.el-checkbox) {
      margin-right: $spacing-unit * 2;
      margin-left: 0;
    }
  }
}

.tw-benchmark-filter-dialog-footer {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
}
</style>
